import { useMutation } from '@apollo/client';
import type { InsertColourPaletteMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertColourPaletteDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { InsertColourPaletteTRPCInput } from './useInsertColourPaletteTRPC';
import { useInsertColourPaletteTRPC } from './useInsertColourPaletteTRPC';

export const useInsertColourPalette = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertColourPaletteGraphQL, graphqlState] = useMutation(
    InsertColourPaletteDocument,
    {
      refetchQueries: [namedOperations.Query.getColourPalettes],
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertColourPaletteTRPC();

  const insertColourPalette = async (
    variables: InsertColourPaletteTRPCInput
  ): Promise<InsertColourPaletteMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertColourPalette(variables);
    }

    const result = await insertColourPaletteGraphQL({
      variables: {
        Name: variables.Name,
        Settings: variables.Settings,
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert colour palette');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertColourPalette,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertColourPalette,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
